import React, { useState, useEffect, useMemo } from "react";
import { Download, Loader2, CheckCircle2, LogOut, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/site/motion";
import Seo from "@/components/site/Seo";
import { API, STORAGE_KEY, withKeys, logError } from "@/components/site/hvac/sections";
import { Gate } from "@/components/site/hvac/Gate";
import { Uploader } from "@/components/site/hvac/Uploader";
import { EditableResultsTable } from "@/components/site/hvac/EditableResultsTable";
import { DebugTrace } from "@/components/site/hvac/DebugTrace";
import { RegistrationBar, FittingsCard, TotalsCards, AnomaliesBox } from "@/components/site/hvac/ResultsPanels";
import { downloadCsv } from "@/components/site/hvac/csv";

const SEO = <Seo title="Computo Canalizzazioni · Area Riservata" description="Strumento tecnico interno di Gruppo CB." path="/tecnico/computo-canali" />;
const sumArea = (rows) => rows.reduce((s, r) => s + (Number(r.area_m2) || 0), 0);

// Re-validate a passcode stored in this tab before showing the upload panel.
function useStoredPasscode() {
    const [passcode, setPasscode] = useState("");
    const [checking, setChecking] = useState(true);
    useEffect(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (!saved || !API) { setChecking(false); return undefined; }
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API}/api/hvac/auth`, { method: "POST", headers: { "X-Tech-Passcode": saved } });
                if (cancelled) return;
                if (r.ok) setPasscode(saved);
                else sessionStorage.removeItem(STORAGE_KEY);
            } catch (e) {
                logError("Tech re-auth failed:", e);
                sessionStorage.removeItem(STORAGE_KEY);
            } finally {
                if (!cancelled) setChecking(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);
    const logout = () => { sessionStorage.removeItem(STORAGE_KEY); setPasscode(""); };
    return { passcode, setPasscode, checking, logout };
}

function ResultsSection({ result, meta }) {
    const [rows, setRows] = useState({ mandata: [], ripresa: [], altro: [] });
    const resetToAuto = () => setRows({ mandata: withKeys(result.mandata), ripresa: withKeys(result.ripresa), altro: withKeys(result.altro) });
    useEffect(resetToAuto, [result]); // eslint-disable-line react-hooks/exhaustive-deps
    const setKind = (kind) => (next) => setRows((r) => ({ ...r, [kind]: next }));
    const totals = useMemo(() => ({ mr: sumArea(rows.mandata) + sumArea(rows.ripresa), altro: sumArea(rows.altro) }), [rows]);

    return (
        <div className="space-y-6" data-testid="analysis-results">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-cb-accent">
                    <CheckCircle2 size={20} />
                    <span className="text-sm font-medium">Stima automatica pronta — <strong>rivedi le lunghezze</strong> prima di esportare</span>
                </div>
                <button onClick={resetToAuto} data-testid="reset-auto" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-cb-accent transition-colors">
                    <RotateCcw size={12} /> Ripristina stime automatiche
                </button>
            </div>
            <div className="text-xs text-slate-500 border-l-2 border-cb-accent pl-3 py-1">
                💡 I canali a doppia linea sono ricostruiti in asse e la larghezza è misurata dal CAD; le quote del PDF servono per l'altezza. Le righe "pezzi speciali" e "flessibile" sono separate: eliminale o modificale se il computo non le richiede. I m² si aggiornano in tempo reale.
            </div>
            <RegistrationBar reg={result.registration} />
            <div className="grid md:grid-cols-2 gap-6">
                <EditableResultsTable title="Mandata (blu)" rows={rows.mandata} onChange={setKind("mandata")} testid="result-mandata" />
                <EditableResultsTable title="Ripresa (rossa)" rows={rows.ripresa} onChange={setKind("ripresa")} testid="result-ripresa" />
                {rows.altro.length > 0 && <EditableResultsTable title="Altra rete (altro colore)" rows={rows.altro} onChange={setKind("altro")} testid="result-altro" />}
                <FittingsCard fittings={result.fittings} />
            </div>
            <TotalsCards result={result} totalMR={totals.mr} totalAltro={totals.altro} hasAltro={rows.altro.length > 0} />
            <AnomaliesBox warnings={result.warnings} anomalies={result.anomalies} />
            {result.debug_trace && <DebugTrace lines={result.debug_trace} meta={meta} />}
            <div>
                <button data-testid="download-csv"
                    onClick={() => downloadCsv({ ...rows, plenum_count: result.plenum_count, griglia_count: result.griglia_count, anomalies: result.anomalies, fittings: result.fittings }, meta)}
                    className="inline-flex items-center gap-2 border border-cb-dark text-cb-dark hover:bg-cb-dark hover:text-white px-6 py-3 font-medium transition-colors">
                    <Download size={16} /> Scarica report CSV
                </button>
            </div>
        </div>
    );
}

export default function ComputoCanali() {
    const { passcode, setPasscode, checking, logout } = useStoredPasscode();
    const [result, setResult] = useState(null);
    const [meta, setMeta] = useState(null);

    if (checking) return (
        <>{SEO}<section data-testid="tech-boot" className="min-h-[70vh] flex items-center justify-center bg-cb-dark"><Loader2 className="text-cb-accent animate-spin" size={32} /></section></>
    );
    if (!passcode) return <>{SEO}<Gate onOk={setPasscode} /></>;

    return (
        <div data-testid="computo-canali-page">
            {SEO}
            <section className="relative pt-40 pb-16 bg-cb-dark cb-grid">
                <div className="max-w-6xl mx-auto px-6 flex items-start justify-between">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">Ufficio Tecnico</Reveal>
                        <Reveal as="h1" delay={0.2} className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-white">Computo Canalizzazioni</Reveal>
                        <Reveal delay={0.4} className="mt-4 max-w-2xl text-slate-300">
                            Carica il DXF dell'impianto (mandata in blu, ripresa in rosso) e il PDF con le quote di sezione: ricostruiamo l'asse dei canali a doppia linea, misuriamo la larghezza reale dal CAD, i flessibili sulle spline e contiamo pezzi speciali e terminali.
                        </Reveal>
                    </div>
                    <button onClick={() => { logout(); setResult(null); setMeta(null); }} data-testid="tech-logout" className="hidden md:inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm">
                        <LogOut size={16} /> Esci
                    </button>
                </div>
            </section>
            <section className="py-12 md:py-16 bg-slate-50">
                <div className="max-w-6xl mx-auto px-6 space-y-10">
                    <div className="bg-white border border-slate-200 p-6 md:p-8">
                        <Uploader passcode={passcode} onResult={(r, m) => { setResult(r); setMeta(m); }} />
                    </div>
                    {result && <ResultsSection result={result} meta={meta} />}
                </div>
            </section>
        </div>
    );
}
